import PropTypes from 'prop-types';
import { useState } from 'react';
import { Button, Col, Input, Popconfirm, Row, Select } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import condStrings from '../../../utils/condStrings';
import PhoneInput from '../../phoneInput';
import DataRowContext from '../DataRowContext';
import StaticData from './StaticData';
import RowCheckbox from '../RowCheckbox';
import {
  CONTACT_MERGE_FIELDS,
  CONTACT_FIELD_LABELS,
  getLessCompleteSide,
} from '../dataRowUtils';

const BOOLEAN_OPTIONS = [
  { value: true, label: 'True' },
  { value: false, label: 'False' },
];

const ContactFieldInput = ({ field, value, options, onChange }) => {
  if (field === 'contact_type_id') {
    return (
      <Select
        size="small"
        placeholder="Type"
        options={options}
        value={value ?? undefined}
        onChange={(val) => onChange(val)}
      />
    );
  }
  if (field === 'is_primary') {
    return (
      <Select
        size="small"
        options={BOOLEAN_OPTIONS}
        value={value ?? false}
        onChange={(val) => onChange(val)}
      />
    );
  }
  if (field === 'phone') {
    return (
      <PhoneInput
        size="small"
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  return (
    <Input
      size="small"
      type={field === 'email' ? 'email' : 'text'}
      placeholder={CONTACT_FIELD_LABELS[field]}
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};

ContactFieldInput.propTypes = {
  field: PropTypes.oneOf(CONTACT_MERGE_FIELDS).isRequired,
  value: PropTypes.any,
  options: PropTypes.array,
  onChange: PropTypes.func.isRequired,
};

ContactFieldInput.defaultProps = {
  value: '',
  options: [],
};

// A single merged-contact comparator, built from the same pieces as a DataRow:
// StaticData on each side (with a per-field checkbox), a RowCheckbox to take a
// whole side (the id/base), and editable inputs in the center. When the two
// contacts are equal there is no conflict, so the sides collapse and only the
// center inputs remain - exactly how an unchanged string row renders.
export default function RowControllerContact(props) {
  const {
    row,
    options,
    onPickWholeSide,
    onPickField,
    onFieldChange,
    onToggleInclude,
    onDelete,
    warnLessComplete,
  } = props;
  const { prev, next, values, checkedSide, included, isOneSided, kind } = row;
  const [deleting, setDeleting] = useState(false);

  const isConflict = kind !== 'exact';
  const hidePrev = !isConflict || !prev;
  const hideNext = !isConflict || !next;
  const isNextLessComplete =
    warnLessComplete && getLessCompleteSide(prev, next) === 'next';

  const contextValue = {
    isConflict,
    type: 'contacts',
    readOnly: false,
    options,
    checkedSide,
    setCheckedSide: () => null,
    selectedItems: { values },
    onItemSelect: (side) => (event) => onPickField(event.target.value, side),
  };

  const handleDelete = () => {
    setDeleting(true);
    // On success the row unmounts, so only reset loading when it fails.
    Promise.resolve(onDelete()).catch(() => setDeleting(false));
  };

  // On a two-sided pair the checkbox picks the base side; on a one-sided row
  // there is only one side, so it doubles as the include/exclude toggle.
  const sideCheckboxProps = (side) =>
    isOneSided
      ? { checked: included, onChange: onToggleInclude }
      : {
          checked: checkedSide === side,
          onChange: () => onPickWholeSide(side),
        };

  return (
    <DataRowContext.Provider value={contextValue}>
      <div
        className={condStrings(
          'data-row-item contact',
          isOneSided && !included && 'is-excluded',
        )}
      >
        <Row align="top" className="mb-2">
          <Col span="7">
            <StaticData side="prev" content={prev} noValues={hidePrev} />
          </Col>
          <Col span="2">
            <RowCheckbox
              side="prev"
              {...sideCheckboxProps('prev')}
              noValues={hidePrev}
            />
          </Col>
          <Col span="6">
            <div className="data-row-input contact">
              {CONTACT_MERGE_FIELDS.map((field) => (
                <div className="data-row-field" key={field}>
                  <span>{CONTACT_FIELD_LABELS[field]}:</span>
                  <ContactFieldInput
                    field={field}
                    value={values[field]}
                    options={options}
                    onChange={(value) => onFieldChange(field, value)}
                  />
                </div>
              ))}
              <div className="data-row-delete">
                <Popconfirm
                  placement="topRight"
                  title="Are you sure you want to delete this contact?"
                  onConfirm={handleDelete}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    htmlType="button"
                    type="defaultx"
                    size="small"
                    danger
                    loading={deleting}
                    icon={<DeleteOutlined />}
                  />
                </Popconfirm>
              </div>
            </div>
          </Col>
          <Col span="2">
            <RowCheckbox
              side="next"
              {...sideCheckboxProps('next')}
              noValues={hideNext}
            />
          </Col>
          <Col span="7">
            <StaticData
              side="next"
              content={next}
              noValues={hideNext}
              showIncomplete={isNextLessComplete}
            />
          </Col>
        </Row>
      </div>
    </DataRowContext.Provider>
  );
}

RowControllerContact.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.string,
    kind: PropTypes.string,
    prev: PropTypes.object,
    next: PropTypes.object,
    values: PropTypes.object,
    checkedSide: PropTypes.oneOf(['prev', 'next']),
    included: PropTypes.bool,
    isOneSided: PropTypes.bool,
  }).isRequired,
  options: PropTypes.array,
  onPickWholeSide: PropTypes.func.isRequired,
  onPickField: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onToggleInclude: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  warnLessComplete: PropTypes.bool,
};

RowControllerContact.defaultProps = {
  options: [],
  warnLessComplete: false,
};
